import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Button, DatePicker, Modal, Progress, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { CalculatorOutlined, LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import { cleanObject } from '@/utils/helpers';
import { checkPermission } from '@/utils/permission';
import { fetchCommissionPeriods, finalizeCommissionAggregates, lockCommissionPeriod } from '@/redux/actions/commissionActions';
import { lifecycleStatus, money } from './commissionLifecycleHelpers';

const { Text } = Typography;

const LabeledValue = ({ label, children, strong = false }) => <div>
    <Text type="secondary">{label}: </Text>
    <Text strong={strong}>{children}</Text>
</div>;

const ProgressValue = ({ label, value, total, status = 'normal' }) => {
    const safeValue = Number(value || 0);
    const safeTotal = Number(total || 0);
    const percent = safeTotal ? Math.round(safeValue * 100 / safeTotal) : 0;
    return <div style={{ width: '100%' }}>
        <Text type="secondary">{label}</Text>
        <Progress
            size="small"
            status={status}
            percent={percent}
            format={() => `${safeValue}/${safeTotal}`}
        />
    </div>;
};

export default function CommissionPeriodPage({ embedded = false }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const allowLock = checkPermission('commission_period', 'lock');
    const allowFinalize = checkPermission('commission_aggregate', 'finalize');
    const result = useSelector(state => state.commissionReducer.periods || { data: [] });
    const query = useMemo(() => qs.parse(location.search.slice(1)), [location.search]);
    const [loading, setLoading] = useState(false);
    const [periodToLock, setPeriodToLock] = useState(null);
    const [locking, setLocking] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            await dispatch(fetchCommissionPeriods({
                period_key: query.period_key || undefined,
                status: query.status || undefined,
                page: Number(query.page) || 1,
                per_page: Number(query.per_page) || 20,
            }));
        }
        catch (error) { /* Action đã hiển thị lỗi. */ }
        finally { setLoading(false); }
    }, [dispatch, query]);
    useEffect(() => { load(); }, [load]);
    const changeFilter = (name, value) => {
        const nextQuery = cleanObject({ ...query, [name]: value, page: 1 });
        history.push({ pathname: location.pathname, search: qs.stringify(nextQuery) });
    };
    const changeTable = tablePagination => {
        const nextPageSize = tablePagination.pageSize || Number(query.per_page) || 20;
        const nextQuery = {
            ...query,
            page: nextPageSize !== (Number(query.per_page) || 20) ? 1 : tablePagination.current,
            per_page: nextPageSize,
        };
        history.push({ pathname: location.pathname, search: qs.stringify(nextQuery) });
    };
    const lock = async () => {
        if (!periodToLock) return;
        setLocking(true);
        try {
            await dispatch(lockCommissionPeriod(periodToLock.period_key, query));
            setPeriodToLock(null);
        }
        catch (error) { /* Action đã hiển thị lỗi. */ }
        finally { setLocking(false); }
    };
    const finalize = async periodKey => {
        try { await dispatch(finalizeCommissionAggregates(periodKey, query)); }
        catch (error) { /* Action đã hiển thị lỗi. */ }
    };
    const sourceStatus = item => {
        const counts = item.processing_job_counts || {};
        const actionableCount = Number(item.actionable_processing_job_count || item.unresolved_source_count || 0);
        const tags = [
            ['pending', 'Chờ bắt đầu', 'gold'],
            ['running', 'Đang xử lý', 'processing'],
            ['stale_running', 'Xử lý bị gián đoạn', 'red'],
            ['waiting', 'Chờ bổ sung thông tin', 'orange'],
            ['failed_retry', 'Cần thử lại', 'volcano'],
        ].filter(([key]) => Number(counts[key]) > 0);
        if (!tags.length) return <Tag color="success">Nguồn đã hoàn tất</Tag>;
        return <Tooltip title={<ul style={{ margin: 0, paddingLeft: 18 }}>
            {(item.lock_blocker_details || []).filter(blocker => blocker.code.indexOf('PROCESSING_JOB_') === 0)
                .map(blocker => <li key={blocker.code}>{blocker.message} {blocker.action}</li>)}
        </ul>}>
            <Space direction="vertical" size={2}>
                <Text strong>Cần xử lý: {actionableCount}</Text>
                {tags.map(([key, label, color]) => (
                    <Tag key={key} color={color}>{label}: {counts[key]}</Tag>
                ))}
            </Space>
        </Tooltip>;
    };
    const columns = [
        {
            title: 'Kỳ áp dụng',
            dataIndex: 'period_key',
            width: 180,
            render: (value, item) => <Space direction="vertical" size={0}>
                <Text strong>Kỳ {moment(value, 'YYYY-MM').format('MM/YYYY')}</Text>
                <Text type="secondary">
                    {moment(item.start_date).format('DD/MM/YYYY')} – {moment(item.end_date).format('DD/MM/YYYY')}
                </Text>
            </Space>,
        },
        {
            title: 'Quy mô',
            key: 'scope',
            width: 135,
            render: (_, item) => <Space direction="vertical" size={0}>
                <LabeledValue label="Nhân sự" strong>{item.employee_count || 0}</LabeledValue>
                <LabeledValue label="Số khoản" strong>{item.item_count || 0}</LabeledValue>
            </Space>,
        },
        {
            title: 'Hoa hồng',
            key: 'amount',
            align: 'right',
            width: 190,
            render: (_, item) => <Space direction="vertical" size={0}>
                <LabeledValue label="Tổng">{money(item.gross_amount)}</LabeledValue>
                <LabeledValue label="Còn lại" strong>{money(item.net_amount)}</LabeledValue>
            </Space>,
        },
        {
            title: 'Tiến độ kiểm soát',
            key: 'control_progress',
            width: 215,
            render: (_, item) => <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <ProgressValue label="Kiểm tra số liệu" value={item.reconciled_count} total={item.item_count} />
                <ProgressValue label="Duyệt" value={item.approved_count} total={item.item_count} status={item.rejected_count ? 'exception' : 'normal'} />
                {Number(item.rejected_count || 0) > 0 && <Text type="danger">Không duyệt: {item.rejected_count}</Text>}
            </Space>,
        },
        {
            title: 'Trạng thái xử lý',
            key: 'status',
            width: 205,
            render: (_, item) => {
                const view = lifecycleStatus(item.status);
                return <Space direction="vertical" size={4}>
                    <Tag color={view.color}>{view.label}</Tag>
                    <Text type="secondary">Nguồn dữ liệu</Text>
                    {sourceStatus(item)}
                </Space>;
            },
        },
        {
            title: 'Chốt kỳ',
            key: 'lock',
            fixed: isMobile ? undefined : 'right',
            width: 290,
            render: (_, item) => item.status === 'LOCKED'
                ? <Space direction="vertical" size={0}>
                    <LabeledValue label="Thời điểm" strong>
                        {item.locked_at ? moment(item.locked_at).format('DD/MM/YYYY HH:mm') : 'Đã chốt kỳ'}
                    </LabeledValue>
                    <LabeledValue label="Người thực hiện">{item.locked_by_name || 'Chưa xác định'}</LabeledValue>
                </Space>
                : <Space wrap>
                    {allowFinalize && <Tooltip title={item.readiness?.is_partial ? 'Chỉ tổng hợp các khoản đã tính xong; kỳ vẫn chưa thể khóa.' : ''}><span><Button disabled={!item.readiness?.can_finalize_completed} icon={<CalculatorOutlined />} onClick={() => finalize(item.period_key)}>{item.readiness?.is_partial ? 'Chốt phần hoàn tất' : 'Chốt tổng hợp'}</Button></span></Tooltip>}
                    {allowLock ? <Tooltip title={!item.can_lock ? <ul style={{ margin: 0, paddingLeft: 18 }}>{(item.lock_blocker_details || []).map(blocker => <li key={blocker.code}>{blocker.message} {blocker.action}</li>)}</ul> : ''}><span><Button type="primary" disabled={!item.can_lock} icon={<LockOutlined />} onClick={() => setPeriodToLock(item)}>Chốt kỳ</Button></span></Tooltip> : null}
                </Space>,
        },
    ];
    const filters = [
        <DatePicker
            key="period_key"
            picker="month"
            value={query.period_key ? moment(query.period_key, 'YYYY-MM') : null}
            format="MM/YYYY"
            placeholder="Kỳ hoa hồng"
            className="table-button"
            style={{ width: '250px' }}
            onChange={value => changeFilter('period_key', value ? value.format('YYYY-MM') : undefined)}
        />,
        <Select
            key="status"
            allowClear
            className="table-button"
            value={query.status || undefined}
            placeholder="Trạng thái kỳ"
            style={{ width: '250px' }}
            onChange={value => changeFilter('status', value)}
            options={['OPEN', 'LOCKED'].map(value => ({ value, label: lifecycleStatus(value).label }))}
        />,
    ];
    return <div>
        {!embedded && <PageTitle routes={[{ path: '/', breadcrumbName: 'Trang chủ' }, { path: '#', breadcrumbName: 'Chốt kỳ hoa hồng' }]} title="Chốt kỳ hoa hồng" />}
        <Alert
            type="info"
            message="Đây là bước kiểm tra tổng thể trước khi kết thúc kỳ. Hãy hoàn tất các khoản còn chờ xử lý, kiểm tra số liệu, duyệt và điều chỉnh; hệ thống chỉ cho Chốt kỳ khi mọi việc cần thiết đã hoàn tất."
            style={{ marginBottom: 16 }}
        />
        <TableActionBar
            isShowAddButton={false}
            isShowDeleteButton={false}
            showSearch={false}
            showFilter={false}
            activeFilter={Boolean(query.period_key || query.status)}
        >
            {filters}
            <Button className="table-button" icon={<ReloadOutlined />} loading={loading} onClick={load}>Làm mới</Button>
        </TableActionBar>
        <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={result.data || []}
            columns={columns}
            scroll={{ x: 'max-content' }}
            pagination={{
                current: result.current_page || Number(query.page) || 1,
                pageSize: result.per_page || Number(query.per_page) || 20,
                total: result.total || 0,
                showTotal: total => `Tổng ${total} kỳ`,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '30', '50', '100'],
            }}
            onChange={changeTable}
        />
        <Modal
            visible={!!periodToLock}
            title={`Xác nhận chốt kỳ ${periodToLock ? moment(periodToLock.period_key, 'YYYY-MM').format('MM/YYYY') : ''}`}
            width={isMobile ? '100%' : '52%'}
            maskClosable={false}
            closable={!locking}
            confirmLoading={locking}
            okText="Chốt kỳ và khóa kết quả"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
            cancelButtonProps={{ disabled: locking }}
            onOk={lock}
            onCancel={() => setPeriodToLock(null)}
        >
            <Alert
                type="warning"
                message={<Text strong>Sau khi chốt, kết quả của kỳ không thể thay đổi.</Text>}
                style={{ marginBottom: 16 }}
            />
            <Text>Các dữ liệu hoa hồng, đối soát, duyệt và kết quả tổng hợp trong kỳ sẽ được xem là kết quả cuối cùng.</Text>
            <ul style={{ marginTop: 12, marginBottom: 0, paddingLeft: 20 }}>
                <li>Chỉ chốt kỳ khi toàn bộ số liệu đã được kiểm tra, đối soát và duyệt đầy đủ, không còn khoản cần xử lý.</li>
                <li style={{ marginTop: 8 }}>Dữ liệu phát sinh hoặc cần điều chỉnh sau thời điểm chốt phải được xử lý theo cơ chế điều chỉnh của kỳ sau, không sửa trực tiếp kết quả kỳ đã chốt.</li>
            </ul>
        </Modal>
    </div>;
}
